import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { setAdminRole } from '../../services/admin/rolesService';
import { adminRoleLabel, classifyAdminCallableError } from './adminUserDisplay';

const ROLE_OPTIONS = ['super_admin', 'admin', 'support', ''];

function newOperationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `role-${Date.now()}`;
}

function roleChangeErrorMessage(kind, t) {
  if (kind === 'permission-denied') return t('admin.users.changeRole.error.denied');
  if (kind === 'not-found') return t('admin.users.changeRole.error.notFound');
  if (kind === 'failed-precondition') return t('admin.users.changeRole.error.lastSuperAdmin');
  if (kind === 'unavailable') return t('admin.users.changeRole.error.unavailable');
  if (kind === 'internal') return t('admin.users.changeRole.error.internal');
  return t('admin.users.changeRole.error');
}

/**
 * Confirmation dialog for setAdminRole. Super_admin-only UX; backend remains the authority.
 */
export default function AdminChangeRoleDialog({ open, user, t, onClose, onSaved }) {
  const { currentUser, refreshAdminRole } = useAuth();
  const [nextRole, setNextRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    setNextRole(user.rentalyzerRole || '');
    setError('');
    setSaving(false);
  }, [open, user]);

  if (!open || !user) return null;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const operationId = newOperationId();
    try {
      await setAdminRole({
        targetUid: user.uid,
        role: nextRole === '' ? null : nextRole,
        operationId,
      });
      if (user.uid === currentUser?.uid) {
        await refreshAdminRole?.();
        window.location.reload();
        return;
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(roleChangeErrorMessage(classifyAdminCallableError(err), t));
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-change-role-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-lg">
        <h2 id="admin-change-role-title" className="text-lg font-semibold text-slate-900">
          {t('admin.users.changeRole')}
        </h2>
        <dl className="mt-4 space-y-2 text-sm">
          <div>
            <dt className="text-slate-500">{t('admin.users.changeRole.user')}</dt>
            <dd className="font-medium text-slate-900">{user.email || user.uid}</dd>
          </div>
          <div>
            <dt className="text-slate-500">{t('admin.users.changeRole.current')}</dt>
            <dd className="text-slate-900">{adminRoleLabel(user.rentalyzerRole, t)}</dd>
          </div>
        </dl>
        <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor="admin-change-role-next">
          {t('admin.users.changeRole.next')}
        </label>
        <select
          id="admin-change-role-next"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={nextRole}
          disabled={saving}
          onChange={(event) => setNextRole(event.target.value)}
        >
          {ROLE_OPTIONS.map((value) => (
            <option key={value || 'none'} value={value}>
              {value ? t(`admin.settings.role.${value}`) : t('admin.users.changeRole.none')}
            </option>
          ))}
        </select>
        {error ? (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            disabled={saving}
            onClick={onClose}
          >
            {t('admin.users.changeRole.cancel')}
          </button>
          <button
            type="button"
            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? t('admin.users.changeRole.saving') : t('admin.users.changeRole.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
